'use client'

import { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { fetchImpersonateStatus } from '@/lib/impersonate-status'
import { compliTierFullLabel } from '@/lib/rbac/compli-tiers'

interface ImpersonationStatus {
  active: boolean
  targetEmail?: string
  targetId?: string
  realEmail?: string
  isAdmin?: boolean
  isRolePreview?: boolean
  previewedRole?: string
}

export function ImpersonationBar() {
  const [status, setStatus] = useState<ImpersonationStatus | null>(null)

  useEffect(() => {
    // Shared with the two mission-rail mounts and the effective-identity /
    // effective-rbac hooks — see @/lib/impersonate-status. Never throws.
    //
    // Inlined rather than called through a useCallback: the indirection made
    // the update look synchronous to the React Compiler
    // (react-hooks/set-state-in-effect) when it is in fact a promise
    // continuation, and it bought nothing — nothing else called it. The
    // cancellation guard is new; a status arriving after unmount used to
    // update a dead component.
    let cancelled = false
    fetchImpersonateStatus().then(({ status: next }) => {
      if (cancelled || !next) return
      setStatus({ ...next, active: Boolean(next.active) })
    })
    return () => { cancelled = true }
  }, [])

  const stopImpersonating = async () => {
    await fetch('/api/admin/impersonate', { method: 'DELETE' })
    Object.keys(localStorage).filter(k => k.includes('crucible') && (k.includes('session') || k.includes('last_session'))).forEach(k => localStorage.removeItem(k))
    sessionStorage.clear()
    // Hard-navigate to a clean URL — without this, the URL's ?session=<id>
    // (referencing the impersonated user's session) would still be picked up
    // by lazy-claim/register after the impersonate cookie is cleared, attributing
    // the impersonated user's session_id to the admin's conversation index.
    window.location.href = '/mainchat'
  }

  const stopPreviewingRole = async () => {
    await fetch('/api/admin/impersonate-role', { method: 'DELETE' })
    window.location.reload()
  }

  // Not admin — render nothing
  if (!status || (!status.active && !status.isAdmin)) return null

  // "View as role" preview — distinct banner, distinct (non-destructive)
  // stop action. Deliberately worded "UI preview" rather than "viewing as",
  // since — unlike real impersonation below — backend API calls still run
  // with the real admin's own permissions (see impersonate.ts).
  if (status.active && status.isRolePreview) {
    const label = status.previewedRole ? compliTierFullLabel(status.previewedRole) : 'a role'
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 bg-sky-500/90 backdrop-blur-sm px-4 py-1.5 text-black text-xs font-medium shadow-lg">
        <Eye className="w-3.5 h-3.5" />
        <span>
          Previewing nav/UI as <strong>{label}</strong>
          <span className="opacity-60 ml-1.5">(UI preview only — your real permissions are unchanged)</span>
        </span>
        <button
          onClick={stopPreviewingRole}
          className="ml-2 flex items-center gap-1 rounded-md bg-black/20 hover:bg-black/30 px-2.5 py-0.5 transition-colors"
        >
          <EyeOff className="w-3 h-3" />
          Stop
        </button>
      </div>
    )
  }

  // Impersonating — show the bar
  if (status.active) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 bg-amber-500/90 backdrop-blur-sm px-4 py-1.5 text-black text-xs font-medium shadow-lg">
        <Eye className="w-3.5 h-3.5" />
        <span>
          Viewing as <strong>{status.targetEmail}</strong>
          <span className="opacity-60 ml-1.5">(logged in as {status.realEmail})</span>
        </span>
        <button
          onClick={stopImpersonating}
          className="ml-2 flex items-center gap-1 rounded-md bg-black/20 hover:bg-black/30 px-2.5 py-0.5 transition-colors"
        >
          <EyeOff className="w-3 h-3" />
          Stop
        </button>
      </div>
    )
  }

  // Admin, not impersonating — the picker lives in MissionRail's nav. This
  // component only renders the "viewing as" banner while impersonation is
  // active; the picker state and its handlers that used to sit here went
  // with the trigger and were left behind unreachable.
  return null
}
